/**
 * Views/interactions for display. Instagram/Facebook/TikTok: prefer `insights`,
 * then last `dailyMetrics`, then root fields. YouTube and others: last `dailyMetrics`,
 * then `insights`, then root (aligned with platform-content mapping).
 */
/** YouTube Analytics API / Reporting OAuth snapshot on private_content.insights */
export type YoutubeDemographicsSnapshot = {
  gender: Array<{ type: string; confidence: number }>;
  ageRanges: Array<{ range: string; confidence: number }>;
};

export interface ContentMetrics {
  views: number;
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  reach: number;
  interactions: number;
  /** For Spotify */
  streams?: number;
  uniqueListeners?: number;
  /** YouTube Analytics OAuth — not in dailyMetrics; from insights */
  uniqueViewers?: number;
  engagedViews?: number;
  thumbnailImpressions?: number;
  thumbnailImpressionsClickRate?: number;
  demographics?: YoutubeDemographicsSnapshot;
}

const emptyMetrics: ContentMetrics = {
  views: 0,
  likes: 0,
  comments: 0,
  shares: 0,
  saves: 0,
  reach: 0,
  interactions: 0,
};

/**
 * Returns the latest daily metric from content.dailyMetrics (last element = most recent).
 * Returns null if dailyMetrics is missing or empty.
 */
/** Merge YouTube OAuth fields from insights (unchanged by daily vs API views path). */
function youtubeOAuthExtras(
  content: any,
  source?: string,
): Partial<
  Pick<
    ContentMetrics,
    | "uniqueViewers"
    | "engagedViews"
    | "thumbnailImpressions"
    | "thumbnailImpressionsClickRate"
    | "demographics"
  >
> {
  if (source !== "youtube" && source !== "youtube_private") return {};
  const ins = content?.insights;
  if (!ins || typeof ins !== "object") return {};

  const out: Partial<ContentMetrics> = {};

  if (ins.uniqueViewers != null && !Number.isNaN(Number(ins.uniqueViewers))) {
    out.uniqueViewers = Number(ins.uniqueViewers);
  }

  if (ins.engagedViews != null && !Number.isNaN(Number(ins.engagedViews))) {
    out.engagedViews = Number(ins.engagedViews);
  }

  if (ins.thumbnailImpressions != null && !Number.isNaN(Number(ins.thumbnailImpressions))) {
    out.thumbnailImpressions = Number(ins.thumbnailImpressions);
  }
  if (
    ins.thumbnailImpressionsClickRate != null &&
    !Number.isNaN(Number(ins.thumbnailImpressionsClickRate))
  ) {
    out.thumbnailImpressionsClickRate = Number(ins.thumbnailImpressionsClickRate);
  }

  const demo = ins.demographics;
  if (
    demo &&
    ((demo.gender?.length ?? 0) > 0 || (demo.ageRanges?.length ?? 0) > 0)
  ) {
    out.demographics = {
      gender: Array.isArray(demo.gender) ? demo.gender : [],
      ageRanges: Array.isArray(demo.ageRanges) ? demo.ageRanges : [],
    };
  }

  return out;
}

/** Multi-line summary for tooltips (Spanish labels). */
export function formatYoutubeDemographicsTooltip(
  d: YoutubeDemographicsSnapshot | undefined,
): string {
  if (!d) return "";
  const genderLabels: Record<string, string> = {
    male: "Masculino",
    female: "Femenino",
    unknown: "Otro",
  };
  const lines: string[] = [];
  if (d.gender?.length) {
    const g = [...d.gender].sort((a, b) => b.confidence - a.confidence);
    lines.push(
      "Género: " +
        g
          .slice(0, 6)
          .map(
            (x) =>
              `${genderLabels[x.type] || x.type}: ${x.confidence.toFixed(1)}%`,
          )
          .join(" · "),
    );
  }
  if (d.ageRanges?.length) {
    const a = [...d.ageRanges].sort((x, y) => {
      const parse = (r: string) =>
        parseInt(r.replace(/^age/i, "").split("-")[0], 10) || 0;
      return parse(x.range) - parse(y.range);
    });
    lines.push(
      "Edad: " +
        a
          .slice(0, 7)
          .map(
            (x) =>
              `${x.range.replace(/^age/i, "")}: ${x.confidence.toFixed(1)}%`,
          )
          .join(" · "),
    );
  }
  return lines.join("\n");
}

/** Map YouTube Analytics age dimension strings into 18–24, 25–34, or 35+ buckets. */
function classifyYoutubeAgeRange(
  range: string,
): "18-24" | "25-34" | "35+" | null {
  const s = range
    .replace(/^age/i, "")
    .replace(/\s/g, "")
    .replace(/–/g, "-")
    .toLowerCase();
  if (/^13[-_]?17|^13-17/.test(s)) return null;
  if (s.includes("18") && s.includes("24")) return "18-24";
  if (s.includes("25") && s.includes("34")) return "25-34";
  if (s.includes("35") && s.includes("44")) return "35+";
  if (s.includes("45") && s.includes("54")) return "35+";
  if (s.includes("55") && s.includes("64")) return "35+";
  if (/65|65\+|65-/.test(s)) return "35+";
  const nums = s.match(/\d+/g)?.map((x) => parseInt(x, 10)) ?? [];
  if (nums.length >= 2) {
    const lo = Math.min(...nums);
    const hi = Math.max(...nums);
    if (hi <= 17) return null;
    if (lo >= 18 && hi <= 24) return "18-24";
    if (lo >= 25 && hi <= 34) return "25-34";
    if (lo >= 35) return "35+";
  }
  return null;
}

export type YoutubeAudienceFiveCols = {
  hombres: number | null;
  mujeres: number | null;
  /** 18–24 */
  edad18a24: number | null;
  /** 25–34 */
  edad25a34: number | null;
  /** 35+ (aggregated bands) */
  edad35plus: number | null;
};

/**
 * Split OAuth demographics into five table columns: Hombres, Mujeres, 18–24, 25–34, 35+.
 */
export function getYoutubeAudienceFiveColumns(
  d: YoutubeDemographicsSnapshot | undefined,
): YoutubeAudienceFiveCols {
  const empty: YoutubeAudienceFiveCols = {
    hombres: null,
    mujeres: null,
    edad18a24: null,
    edad25a34: null,
    edad35plus: null,
  };
  if (!d) return { ...empty };

  let hombres: number | null = null;
  let mujeres: number | null = null;
  for (const g of d.gender || []) {
    if (g.type === "male") hombres = g.confidence;
    if (g.type === "female") mujeres = g.confidence;
  }

  let s18 = 0;
  let s25 = 0;
  let s35 = 0;
  let c18 = false;
  let c25 = false;
  let c35 = false;
  for (const a of d.ageRanges || []) {
    const bucket = classifyYoutubeAgeRange(a.range);
    if (bucket === "18-24") {
      s18 += a.confidence;
      c18 = true;
    } else if (bucket === "25-34") {
      s25 += a.confidence;
      c25 = true;
    } else if (bucket === "35+") {
      s35 += a.confidence;
      c35 = true;
    }
  }

  return {
    hombres,
    mujeres,
    edad18a24: c18 ? s18 : null,
    edad25a34: c25 ? s25 : null,
    edad35plus: c35 ? s35 : null,
  };
}

export function formatYoutubeAudiencePercent(value: number | null): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `${value.toLocaleString("es-ES", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

/** YouTube OAuth audience retention curve point (ratios 0–1). */
export type AudienceRetentionCurvePoint = {
  elapsedVideoTimeRatio: number;
  audienceWatchRatio: number;
};

export type EpisodeRetentionAtQuarters = {
  retention25: number | null;
  retention50: number | null;
  retention75: number | null;
};

/**
 * Sample audienceWatchRatio (as 0–100%) at 25% / 50% / 75% of episode duration
 * using the closest curve point (same approach as getEstimatedViewersAtTime).
 */
export function getAudienceRetentionAtRatios(
  curve:
    | AudienceRetentionCurvePoint[]
    | Array<{ elapsedVideoTimeRatio?: number; audienceWatchRatio?: number }>
    | null
    | undefined,
  ratios: readonly [number, number, number] = [0.25, 0.5, 0.75],
): EpisodeRetentionAtQuarters {
  const empty: EpisodeRetentionAtQuarters = {
    retention25: null,
    retention50: null,
    retention75: null,
  };
  if (!Array.isArray(curve) || curve.length === 0) return empty;

  const points = curve
    .map((p) => ({
      elapsedVideoTimeRatio: Number(p?.elapsedVideoTimeRatio),
      audienceWatchRatio: Number(p?.audienceWatchRatio),
    }))
    .filter(
      (p) =>
        Number.isFinite(p.elapsedVideoTimeRatio) &&
        Number.isFinite(p.audienceWatchRatio),
    );
  if (points.length === 0) return empty;

  const sampleAt = (targetRatio: number): number | null => {
    if (!Number.isFinite(targetRatio)) return null;
    const closest = points.reduce((prev, curr) =>
      Math.abs(curr.elapsedVideoTimeRatio - targetRatio) <
      Math.abs(prev.elapsedVideoTimeRatio - targetRatio)
        ? curr
        : prev,
    );
    return Math.round(closest.audienceWatchRatio * 1000) / 10;
  };

  return {
    retention25: sampleAt(ratios[0]),
    retention50: sampleAt(ratios[1]),
    retention75: sampleAt(ratios[2]),
  };
}

function isoTimestamp(value: unknown): string | null {
  if (value == null || value === "") return null;
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

function collectedAtOfLatestDaily(content: any): string | null {
  const dailyMetrics = content?.dailyMetrics;
  if (!Array.isArray(dailyMetrics) || dailyMetrics.length === 0) return null;
  return isoTimestamp(dailyMetrics[dailyMetrics.length - 1]?.collectedAt);
}

/** Timestamp of `insights` when those fields are the metrics source (OAuth / manual). */
export function getInsightsCollectedAt(content: any): string | null {
  const insights = content?.insights;
  if (!insights || typeof insights !== "object" || Array.isArray(insights)) {
    return null;
  }
  return isoTimestamp(insights.collectedAt ?? insights.uploadedAt);
}

/**
 * When the numbers in `getLatestContentMetrics` were collected.
 * Matches that function's source order: daily snapshot for YouTube-style rows,
 * insights first for Instagram, Facebook, and TikTok.
 */
export function getContentMetricsUpdatedAt(
  content: any,
  source?: string,
): string | null {
  if (!content) return null;
  const src = String(source || content.source || "").toLowerCase();
  const insightsFirst =
    src === "instagram" || src === "facebook" || src === "tiktok";

  if (insightsFirst) {
    const ins = content.insights;
    if (ins != null && typeof ins === "object" && !Array.isArray(ins)) {
      return getInsightsCollectedAt(content);
    }
    return collectedAtOfLatestDaily(content);
  }

  return collectedAtOfLatestDaily(content) ?? getInsightsCollectedAt(content);
}

function getLatestDailyMetric(content: any): { views: number; likes: number; comments: number } | null {
  const dailyMetrics = content?.dailyMetrics;
  if (!Array.isArray(dailyMetrics) || dailyMetrics.length === 0) return null;
  // Assume appended chronologically; last element is latest
  const latest = dailyMetrics[dailyMetrics.length - 1];
  return {
    views: Number(latest?.views) || 0,
    likes: Number(latest?.likes) || 0,
    comments: Number(latest?.comments) || 0,
  };
}

function buildMetricsFromLatestDaily(content: any, source?: string): ContentMetrics | null {
  const latestDaily = getLatestDailyMetric(content);
  if (!latestDaily) return null;
  const likes = latestDaily.likes;
  const comments = latestDaily.comments;
  const interactions = source === "youtube" ? likes + comments : likes + comments;
  return {
    views: latestDaily.views,
    likes,
    comments,
    shares: 0,
    saves: 0,
    reach: 0,
    interactions,
    ...youtubeOAuthExtras(content, source),
  };
}

function buildMetricsFromInsights(content: any, source?: string): ContentMetrics | null {
  const insights = content.insights;
  if (!insights || typeof insights !== "object") return null;
  const views = Number(insights.views) || 0;
  const likes = Number(insights.likes) || 0;
  const comments = Number(insights.comments) || 0;
  const shares = Number(insights.shares) || 0;
  const saves = Number(insights.saves) || 0;
  const reach = Number(insights.reach) || 0;
  const interactions =
    source === "youtube"
      ? likes + comments
      : likes + comments + shares + saves;
  return {
    views,
    likes,
    comments,
    shares,
    saves,
    reach,
    interactions,
    streams: insights.streams != null ? Number(insights.streams) : undefined,
    uniqueListeners:
      insights.uniqueListeners != null ? Number(insights.uniqueListeners) : undefined,
    ...youtubeOAuthExtras(content, source),
  };
}

function buildMetricsFromRoot(content: any, source?: string): ContentMetrics {
  const views = Number(content.views) || 0;
  const likes = Number(content.likes) || 0;
  const comments = Number(content.comments) || 0;
  const shares = Number(content.shares) || 0;
  const saves = Number(content.saves) || 0;
  const reach = Number(content.reach) || 0;
  const interactions =
    source === "youtube" ? likes + comments : likes + comments + shares + saves;
  return {
    views,
    likes,
    comments,
    shares,
    saves,
    reach,
    interactions,
    ...youtubeOAuthExtras(content, source),
  };
}

export function getLatestContentMetrics(content: any, source?: string): ContentMetrics {
  if (!content) return { ...emptyMetrics };

  const src = String(source || content.source || "").toLowerCase();
  const insightsFirst =
    src === "instagram" || src === "facebook" || src === "tiktok";

  if (insightsFirst) {
    const ins = content.insights;
    if (ins != null && typeof ins === "object" && !Array.isArray(ins)) {
      return (
        buildMetricsFromInsights(content, source) ?? buildMetricsFromRoot(content, source)
      );
    }
    return (
      buildMetricsFromLatestDaily(content, source) ??
      buildMetricsFromRoot(content, source)
    );
  }

  return (
    buildMetricsFromLatestDaily(content, source) ??
    buildMetricsFromInsights(content, source) ??
    buildMetricsFromRoot(content, source)
  );
}
